import { useState, useCallback, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { avatarRecipe, avatarImg } from './avatar.css';

export type AvatarSize    = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type AvatarVariant = 'circular' | 'square' | 'rounded';

export interface AvatarProps {
  src?: string;
  alt?: string;
  size?:    AvatarSize;
  variant?: AvatarVariant;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * Styles applied ONLY once the image has actually painted — merged over
   * `style`. Chrome that frames a photo (a border, a photo-sized box, an
   * opaque backdrop) belongs here, not in `style`: while the image is still
   * loading the fallback must look exactly like the no-image state, and a
   * frame drawn around it gives that state away.
   */
  loadedStyle?: CSSProperties;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}

export function Avatar({
  src,
  alt,
  size    = 'md',
  variant = 'circular',
  children,
  className,
  style,
  loadedStyle,
  onClick,
}: AvatarProps) {
  // R1 (#358): loaded-ness is derived from the src it was observed for.
  // A src change hides the image with no state write and no ordering
  // dependency — no useEffect that could race with onLoad.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  // F5 (secondary): on image error, fall through to the children fallback.
  // Track which src failed so a new src is not marked as failed.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const imageFailed = Boolean(src && failedSrc === src);
  const imageLoaded = Boolean(src && loadedSrc === src);
  const showImage = src && !imageFailed;
  const imageVisible = Boolean(showImage && imageLoaded);

  // R2 (#358): belt-and-braces for already-decoded images. A cached image
  // (or a tiny data-URL) may not re-fire `load` at all. A ref callback runs
  // synchronously during render commit, before passive effects, so it catches
  // the case where `img.complete && img.naturalWidth > 0` on mount.
  const imgRefCallback = useCallback((img: HTMLImageElement | null) => {
    if (img && img.complete && img.naturalWidth > 0 && img.src) {
      // The src attribute on the DOM node is the absolute URL; compare against
      // the prop value that React set. For data-URLs they match exactly; for
      // relative URLs the DOM absolutises, but Avatar always receives absolute
      // URLs or data-URLs, so a direct comparison is safe. Fall back to
      // accepting any complete image if the comparison fails.
      setLoadedSrc(src ?? null);
    }
  }, [src]);

  // F5: when onClick is provided, make the element keyboard-activatable.
  const isClickable = Boolean(onClick);
  const handleKeyDown = isClickable && onClick
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as MouseEvent<HTMLDivElement>);
        }
      }
    : undefined;

  return (
    <div
      className={clsx(avatarRecipe({ size, variant }), className)}
      style={imageVisible && loadedStyle ? { ...style, ...loadedStyle } : style}
      aria-label={alt}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {/* Fallback layer (initials / sentiment icon) — beneath the image, and
          the ONLY thing visible until the image finishes loading or if it
          fails. The loaded image (opaque, object-fit: cover) covers it. */}
      {children ?? null}
      {showImage && (
        <img
          ref={imgRefCallback}
          src={src}
          alt={alt ?? ''}
          className={avatarImg}
          style={{ opacity: imageLoaded ? 1 : 0 }}
          onLoad={() => setLoadedSrc(src!)}
          onError={() => setFailedSrc(src!)}
        />
      )}
    </div>
  );
}
